// Pure notification → link → route parsing, pulled out of AvisosPage.tsx.
// This is the highest-risk piece of the notification pipeline: every push
// the backend sends (raffle settlement, feedback replies, wallet events...)
// has to land on the exact right screen, not just a plausible one. Keeping
// it a standalone module with no React/store dependencies means it can be
// unit-tested against the literal `action_uri` strings the backend emits,
// independent of rendering.
import type { PlayerRoute } from '../../app/appStore';
import type { NotificationItem } from './api';

export function linkFromParams(params: NotificationItem['params']) {
  if (!params) return '';
  try {
    const value = typeof params === 'string' ? JSON.parse(params) as Record<string, unknown> : params;
    const direct = [
      value.action,
      value.action_uri,
      value.actionUri,
      value.link,
      value.url,
      value.href,
      value.deep_link,
      value.deepLink,
      value.route,
      value.screen,
      value.target,
      value.cta_action,
      value.ctaAction,
    ].find((item) => typeof item === 'string' && item.trim());
    if (typeof direct !== 'string') return '';
    const eventId = [
      value.raffle_event_id,
      value.raffleEventId,
      value.event_id,
      value.eventId,
      value.event,
    ].find((item) => item != null && String(item).trim());
    return eventId && !/[?&#](?:raffle_event_id|raffleEventId|event_id|eventId|event)=/i.test(direct)
      ? `${direct}${direct.includes('?') ? '&' : '?'}event_id=${encodeURIComponent(String(eventId))}`
      : direct;
  } catch {
    return '';
  }
}

export function notificationLink(item: NotificationItem) {
  const link = item.link || linkFromParams(item.params);
  const eventId = raffleEventIdFromNotificationItem(item);
  if (!link && eventId && isRaffleRevealNotification(item)) return `open_raffle_reveal?event_id=${encodeURIComponent(eventId)}`;
  if (!link || !eventId || /[?&#](?:raffle_event_id|raffleEventId|event_id|eventId|event)=/i.test(link)) return link;
  if (!hasAny(link.toLowerCase(), ['raffle-reveal', 'raffle-result', 'open_raffle_reveal', 'open_raffle_result', 'sorteio-horario', 'wallet/tickets'])) return link;
  return `${link}${link.includes('?') ? '&' : '?'}event_id=${encodeURIComponent(eventId)}`;
}

function isRaffleRevealNotification(item: NotificationItem) {
  const marker = `${item.type || ''} ${item.template_id || ''}`.toLowerCase().replace(/_/g, '-');
  return hasAny(marker, ['raffle-reveal', 'raffle-result', 'raffle-settled', 'hourly-raffle-result']);
}

function raffleEventIdFromNotificationItem(item: NotificationItem) {
  const direct = item.raffle_event_id || item.raffleEventId || item.event_id || item.eventId;
  if (direct != null && String(direct).trim()) return String(direct);
  try {
    const params = typeof item.params === 'string' ? JSON.parse(item.params) as Record<string, unknown> : item.params;
    if (!params) return '';
    const value = params.raffle_event_id || params.raffleEventId || params.event_id || params.eventId || params.event;
    return value != null && String(value).trim() ? String(value) : '';
  } catch {
    return '';
  }
}

export type NotificationRouteTarget = {
  route: PlayerRoute;
  roomId?: string;
  orderId?: string;
  profileUserId?: string;
  archiveRoundNumber?: string;
  walletAction?: 'deposit' | 'withdraw';
  securityChannel?: 'phone' | 'email';
  walletTicketsView?: 'register' | 'participants' | 'result';
  walletTicketsResultId?: string | null;
  // Support-reply deep link (Task 17): `/#/avisos?tab=feedback&id=<id>`, the
  // shape AdminFeedbackController writes on every reply. Matches the raffle
  // pattern already proven in production (RaffleSettleScheduler's
  // `/#/wallet?tab=Bilhetes&event=...`) — a hash-router path plus query.
  feedbackId?: number;
};

export function routeFromLink(link: string): NotificationRouteTarget | null {
  if (!link) return null;
  const normalized = normalizeNotificationLink(link);
  const unpaid = unpaidDepositsTargetFromLink(normalized);
  if (unpaid) return unpaid;
  const walletTicketsAction = walletTicketsTargetFromNotificationAction(normalized);
  if (walletTicketsAction) return { route: 'wallet.tickets', ...walletTicketsAction };
  const actionRoute = routeFromNotificationAction(normalized);
  if (actionRoute) return { route: actionRoute };
  const roomId = roomIdFromNotificationLink(normalized);
  if (roomId) return { route: 'room', roomId };
  const orderId = orderIdFromNotificationLink(normalized);
  if (orderId) return { route: 'me.history', orderId };
  const walletAction = walletActionFromNotificationLink(normalized);
  if (walletAction) return { route: 'wallet', walletAction };
  const walletTicketsTarget = walletTicketsTargetFromNotificationLink(normalized);
  if (walletTicketsTarget) return { route: 'wallet.tickets', ...walletTicketsTarget };
  const profileUserId = profileUserIdFromNotificationLink(normalized);
  if (profileUserId) return { route: 'leaderboard', profileUserId };
  const archiveRoundNumber = archiveRoundFromNotificationLink(normalized);
  if (archiveRoundNumber) return { route: 'archive', archiveRoundNumber };
  const securityChannel = securityChannelFromNotificationLink(normalized);
  if (securityChannel) return { route: 'me.security', securityChannel };
  // Support-reply deep link. Checked before the generic 'avisos' fallback
  // below (which would otherwise match the same `/#/avisos...` path first
  // and drop the caller on the plain Notices tab instead of the Feedback
  // thread the player was actually notified about).
  if (/[?&#]tab=feedback/i.test(normalized)) {
    const feedbackId = Number(normalized.match(/[?&#]id=(\d+)/)?.[1] || 0);
    return { route: 'avisos.feedback', feedbackId: feedbackId || undefined };
  }
  if (hasAny(normalized, ['bilhetes', 'tickets', 'raffle', 'sorteio-horario'])) return { route: 'wallet.tickets' };
  if (hasAny(normalized, ['pedidos', 'orders', 'withdraw', 'deposit-order', 'saque', 'pedido'])) return { route: 'pedidos' };
  if (hasAny(normalized, ['bet-history', 'my-bets', 'minhas-apostas', 'mis-apuestas'])) return { route: 'bet-history' };
  if (hasAny(normalized, ['archive', 'draw-archive', 'historico-sorteios', 'historial-sorteos'])) return { route: 'archive' };
  if (hasAny(normalized, ['rules', 'verify', 'verificar', 'transparency'])) return { route: 'rules' };
  if (hasAny(normalized, ['wallet', 'carteira', 'billetera'])) return { route: 'wallet' };
  if (hasAny(normalized, ['leaderboard', 'ranking', 'top'])) return { route: 'leaderboard' };
  if (hasAny(normalized, ['avisos/faq', 'faq'])) return { route: 'avisos.faq' };
  if (hasAny(normalized, ['avisos', 'notifications', 'notices'])) return { route: 'avisos' };
  if (hasAny(normalized, ['me/security', 'security', 'seguranca', 'seguridad'])) return { route: 'me.security' };
  if (hasAny(normalized, ['me/kyc', 'kyc', 'identity-verification', 'identity'])) return { route: 'me.kyc' };
  if (hasAny(normalized, ['me/invite', 'invite', 'convite', 'invitacion'])) return { route: 'me.invite' };
  if (hasAny(normalized, ['me/activities', 'activities', 'atividades', 'actividades'])) return { route: 'me.activities' };
  if (hasAny(normalized, ['me/limits', 'responsible', 'limites', 'limites-jogo'])) return { route: 'me.limits' };
  if (hasAny(normalized, ['me/vip', 'vip'])) return { route: 'me.vip' };
  if (hasAny(normalized, ['me/support', 'support', 'suporte', 'soporte', 'help'])) return { route: 'avisos.help' };
  if (hasAny(normalized, ['me/profile', 'profile', 'perfil', 'conta'])) return { route: 'me.profile' };
  if (hasAny(normalized, ['me'])) return { route: 'me' };
  if (hasAny(normalized, ['hall', 'lobby', 'salas'])) return { route: 'lobby' };
  return null;
}

/** `#/pedidos/unpaid`, `#/pedidos/unpaid/<id>`, `...?order=<id>` — the manual-proof recovery page. */
function unpaidDepositsTargetFromLink(link: string): NotificationRouteTarget | null {
  if (!/(?:^|[/#])(?:pedidos|orders|pedido)\/unpaid(?:[/?#]|$)/i.test(link)) return null;
  const fromQuery = link.match(/[?&#]order(?:_id|Id)?=([^&#]+)/i)?.[1];
  const fromPath = link.match(/(?:pedidos|orders|pedido)\/unpaid\/([^/?#]+)/i)?.[1];
  const raw = fromQuery || fromPath || '';
  const orderId = raw ? safeDecodeLink(raw) : '';
  return orderId ? { route: 'pedidos.unpaid', orderId } : { route: 'pedidos.unpaid' };
}

function routeFromNotificationAction(link: string): PlayerRoute | null {
  const action = notificationActionValue(link);
  const value = action.startsWith('nav_') ? action.slice(4).replace(/_/g, '-') : action.replace(/_/g, '-');
  if (value === 'wallet' || value === 'carteira' || value === 'billetera') return 'wallet';
  if (value === 'wallet-tickets' || value === 'tickets' || value === 'bilhetes') return 'wallet.tickets';
  if (value === 'unpaid-deposits' || value === 'pedidos-unpaid' || value === 'deposit-proof') return 'pedidos.unpaid';
  if (value === 'orders' || value === 'pedidos') return 'pedidos';
  if (value === 'tournament' || value === 'leaderboard' || value === 'ranking' || value === 'top') return 'leaderboard';
  if (value === 'avisos' || value === 'notifications' || value === 'notices') return 'avisos';
  if (value === 'faq' || value === 'avisos-faq') return 'avisos.faq';
  if (value === 'rules' || value === 'fairness' || value === 'provably-fair') return 'rules';
  if (value === 'archive' || value === 'history') return 'archive';
  if (value === 'hall' || value === 'lobby') return 'lobby';
  if (value === 'me' || value === 'profile-center') return 'me';
  if (value === 'profile' || value === 'me-profile') return 'me.profile';
  if (value === 'vip' || value === 'me-vip') return 'me.vip';
  if (value === 'limits' || value === 'responsible' || value === 'responsible-gaming') return 'me.limits';
  if (value === 'activities' || value === 'promos' || value === 'promocoes') return 'me.activities';
  if (value === 'invite' || value === 'referral') return 'me.invite';
  if (value === 'security') return 'me.security';
  if (value === 'kyc' || value === 'identity' || value === 'identity-verification') return 'me.kyc';
  if (value === 'support' || value === 'help') return 'avisos.help';
  if (value === 'bet-history' || value === 'my-bets') return 'bet-history';
  return null;
}

function walletTicketsTargetFromNotificationAction(link: string): { walletTicketsView: 'register' | 'participants' | 'result'; walletTicketsResultId?: string | null } | null {
  const value = notificationActionValue(link).replace(/_/g, '-');
  const eventId = raffleEventIdFromNotificationLink(link);
  if (['open-raffle-register', 'raffle-register', 'raffle-registration', 'open-hourly-raffle'].includes(value)) {
    return { walletTicketsView: 'register' };
  }
  if (['open-raffle-participants', 'raffle-participants', 'raffle-entries'].includes(value)) {
    return { walletTicketsView: 'participants' };
  }
  if (['open-raffle-reveal', 'open-raffle-result', 'open-hourly-raffle-result', 'raffle-reveal', 'raffle-result', 'hourly-raffle-result', 'raffle-settled'].includes(value)) {
    return { walletTicketsView: 'result', walletTicketsResultId: eventId || null };
  }
  return null;
}

function notificationActionValue(link: string) {
  return safeDecodeLink(
    link.match(/[?&#](?:action|cta_action|ctaAction|route|screen|target)=([^&#]+)/i)?.[1] ||
    link.split(/[?#]/)[0].split('/').filter(Boolean).pop() ||
    link,
  ).toLowerCase();
}

function normalizeNotificationLink(link: string) {
  const value = link.trim();
  try {
    const url = new URL(value, typeof window === 'undefined' ? 'https://vgmable.baby' : window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
}

function roomIdFromNotificationLink(link: string) {
  const roomPath = link.match(/(?:#\/|\/)?room\/([\w-]+)/i)?.[1];
  const roomQuery = link.match(/[?&#](?:room|room_id|roomid)=([\w-]+)/i)?.[1];
  const value = roomPath || roomQuery;
  if (!value) return null;
  return value.startsWith('room-') ? value : `room-${value}`;
}

function orderIdFromNotificationLink(link: string) {
  const orderPath = link.match(/(?:#\/|\/)?pedidos\/([^/?#&]+)/i)?.[1] || link.match(/(?:#\/|\/)?orders\/([^/?#&]+)/i)?.[1];
  const orderQuery = link.match(/[?&#](?:order|order_id|orderId|pedido|pedido_id|deposit_id|withdraw_id)=([^&#]+)/i)?.[1];
  const value = orderPath || orderQuery;
  if (!value) return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function walletActionFromNotificationLink(link: string): 'deposit' | 'withdraw' | null {
  const path = link.match(/(?:#\/|\/)?wallet\/([^/?#&]+)/i)?.[1] ||
    link.match(/(?:#\/|\/)?carteira\/([^/?#&]+)/i)?.[1] ||
    link.match(/(?:#\/|\/)?billetera\/([^/?#&]+)/i)?.[1];
  const query = link.match(/[?&#](?:wallet_action|walletAction|action)=([^&#]+)/i)?.[1];
  const value = safeDecodeLink(path || query || '').toLowerCase();
  if (['deposit', 'deposito', 'pix-deposit', 'topup', 'recharge'].includes(value) || hasAny(link, ['open-deposit', 'pix-deposit'])) return 'deposit';
  if (['withdraw', 'saque', 'pix-withdraw', 'cashout'].includes(value) || hasAny(link, ['open-withdraw', 'pix-withdraw'])) return 'withdraw';
  return null;
}

function profileUserIdFromNotificationLink(link: string) {
  const profilePath = link.match(/(?:#\/|\/)?(?:leaderboard|ranking|top)\/(?:user|profile|player)\/([^/?#&]+)/i)?.[1] ||
    link.match(/(?:#\/|\/)?(?:users|players)\/([^/?#&]+)\/public-profile/i)?.[1];
  const profileQuery = link.match(/[?&#](?:profile_user_id|profileUserId|public_profile_user_id|user_id|userId|player_id|playerId)=([^&#]+)/i)?.[1];
  const value = profilePath || profileQuery;
  return value ? safeDecodeLink(value) : null;
}

function archiveRoundFromNotificationLink(link: string) {
  const roundPath = link.match(/(?:#\/|\/)?(?:archive|draw-archive|historico-sorteios|historial-sorteos)\/(?:round|draw)\/([^/?#&]+)/i)?.[1] ||
    link.match(/(?:#\/|\/)?(?:archive|draw-archive|historico-sorteios|historial-sorteos)\/(\d+)/i)?.[1];
  const roundQuery = link.match(/[?&#](?:round|round_number|roundNumber|draw|draw_id|drawId)=([^&#]+)/i)?.[1];
  const value = roundPath || roundQuery;
  return value ? safeDecodeLink(value) : null;
}

function securityChannelFromNotificationLink(link: string): 'phone' | 'email' | null {
  const path = link.match(/(?:#\/|\/)?me\/security\/([^/?#&]+)/i)?.[1] ||
    link.match(/(?:#\/|\/)?(?:security|verify|verification|contact)\/([^/?#&]+)/i)?.[1];
  const query = link.match(/[?&#](?:security_channel|securityChannel|verify|verify_channel|verifyChannel|contact|channel)=([^&#]+)/i)?.[1];
  const value = safeDecodeLink(path || query || '').toLowerCase();
  if (['phone', 'telefone', 'tel', 'mobile', 'celular'].includes(value) || hasAny(link, ['verify-phone', 'phone-verify', 'telefone'])) return 'phone';
  if (['email', 'e-mail', 'mail'].includes(value) || hasAny(link, ['verify-email', 'email-verify', 'e-mail'])) return 'email';
  return null;
}

function walletTicketsTargetFromNotificationLink(link: string): { walletTicketsView: 'register' | 'participants' | 'result'; walletTicketsResultId?: string | null } | null {
  const path = link.match(/(?:#\/|\/)?wallet\/tickets\/([^/?#&]+)(?:\/([^/?#&]+))?/i) ||
    link.match(/(?:#\/|\/)?bilhetes\/([^/?#&]+)(?:\/([^/?#&]+))?/i) ||
    link.match(/(?:#\/|\/)?tickets\/([^/?#&]+)(?:\/([^/?#&]+))?/i);
  const action = safeDecodeLink(path?.[1] || '').toLowerCase();
  const eventId = raffleEventIdFromNotificationLink(link);
  const tab = safeDecodeLink(link.match(/[?&#](?:tab|view)=([^&#]+)/i)?.[1] || '').toLowerCase();
  const id = path?.[2] || eventId;
  if (eventId && (hasAny(link, ['raffle-reveal', 'raffle-result', 'hourly-raffle-result', 'open_raffle_reveal', 'open_raffle_result', 'open_hourly_raffle_result']) || tab === 'bilhetes' || tab === 'tickets')) {
    return { walletTicketsView: 'result', walletTicketsResultId: eventId };
  }
  if (action === 'raffle' || action === 'register') return { walletTicketsView: 'register' };
  if (action === 'participants') return { walletTicketsView: 'participants' };
  if (action === 'result' && id) return { walletTicketsView: 'result', walletTicketsResultId: safeDecodeLink(id) };
  if (hasAny(link, ['raffle-participants', 'participants-list'])) return { walletTicketsView: 'participants' };
  if (hasAny(link, ['raffle-register', 'hourly-raffle'])) return { walletTicketsView: 'register' };
  return null;
}

function raffleEventIdFromNotificationLink(link: string) {
  const value = link.match(/[?&#](?:raffle_event_id|raffleEventId|event_id|eventId|event)=([^&#]+)/i)?.[1] ||
    link.match(/(?:#\/|\/)?(?:raffle|sorteio-horario)\/(?:result|reveal|event)\/([^/?#&]+)/i)?.[1] ||
    '';
  return value ? safeDecodeLink(value) : '';
}

function safeDecodeLink(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function hasAny(link: string, patterns: string[]) {
  const value = link.toLowerCase();
  return patterns.some((pattern) => value.includes(pattern.toLowerCase()));
}
